import React, { useState, useEffect, useRef } from 'react';
import { Tag, TagGroup, IconButton } from 'rsuite';
import PlusIcon from '@rsuite/icons/Plus';
import { ErrorContainer } from '../../developers/page-components/ErrorContainer';
import clsx from 'clsx';
import { WebsiteInput } from '../WebsiteInput';

import './UrlList.scss';
import { validateUrl } from '../../../utils/validators';

interface Props {
    isAdmin: boolean;
    urls: string[];
    setUrls: (value: string[]) => void;
    formik: any;
    fieldName: string;
    title?: string;
    label: string;
    isRequired?: boolean;
    isEditMode: boolean;
}

export const UrlList = (props: Props): React.ReactElement => {
    const { isAdmin, urls, setUrls, formik, fieldName, title, label, isRequired, isEditMode } = props;
    const [typing, setTyping] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>('');
    const [tagInputValue, setTagInputValue] = useState<string>('');
    const [index, setIndex] = useState<number | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const urlInputRef = useRef<HTMLInputElement>(null);


    useEffect(() => {
        if (index !== null && urlInputRef.current) {
            urlInputRef.current.focus();
        }
    }, [index]);

    const addUrl = (): void => {
        const trimmedInputValue = inputValue.trim();
        const trimmedTagInputValue = tagInputValue.trim();

        if (index !== null && !validateUrl(trimmedTagInputValue) || index === null && !validateUrl(trimmedInputValue)) {
            return;
        }

        const nextUrls = [...urls];

        if (index !== null && trimmedTagInputValue.length === 0) {
            setIndex(null);
            setTagInputValue('');
        } else if (index !== null && trimmedTagInputValue.length > 0 && trimmedTagInputValue !== urls[index]) {
            nextUrls[index] = trimmedTagInputValue;
            setIndex(null);
            setTagInputValue('');
            setUrls(nextUrls);
            formik.setFieldValue(fieldName, nextUrls);
        } else if (index !== null && trimmedTagInputValue.length > 0 && trimmedTagInputValue === urls[index]) {
            setIndex(null);
        } else if (index === null && trimmedInputValue.length > 0) {
            nextUrls.push(trimmedInputValue);
            setInputValue('');
            setUrls(nextUrls);
            formik.setFieldValue(fieldName, nextUrls);
        }
    };

    const handleButtonClick = (): void => {
        if (index !== null && tagInputValue.trim()) {
            addUrl();
        } else if (inputValue.trim()) {
            addUrl();
        }
    };

    const getErrorMessage = (): React.ReactElement => {
        const trimmedInputValue = inputValue.trim();
        const trimmedTagInputValue = tagInputValue.trim();

        const isInnerError = formik.touched[fieldName] && formik.errors[fieldName] && (index !== null && trimmedInputValue.length > 0 && !validateUrl(trimmedTagInputValue) || index === null && trimmedInputValue.length > 0 && !validateUrl(trimmedInputValue));

        if (isInnerError) {
            return (
                <div className="error-container position-absolute">
                    <div className="text-danger fs-8">Please enter a valid site url (e.g. https://www.example.com)</div>
                </div>
            );
        }

        return <ErrorContainer formik={formik} fieldName={fieldName} additionClass="error-container position-absolute" />;
    };

    const removeUrl = (e: React.MouseEvent<HTMLElement, MouseEvent>, url: string): void => {
        const nextUrls = urls.filter(item => item !== url);
        formik.setFieldValue(fieldName, nextUrls);
        setUrls(nextUrls);
        setTyping(nextUrls.length === 0);
        setIndex(null);
    };

    const handleTagClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, i: number): void => {
        const svgElement = e.currentTarget.querySelector('svg[aria-label="close"]');
        if (svgElement && svgElement.contains(e.target as Node)) {
            return;
        }
        setIndex(i);
        setTagInputValue(urls[i]);
    };

    const renderTagInput = (): JSX.Element => (
        <div className="row mb-5 ">
            <div className="col-7 col-sm-7 col-md-5 col-lg-4">
                <WebsiteInput
                    ref={urlInputRef}
                    value={tagInputValue}
                    onChangeCallback={(value): void => setTagInputValue(value)}
                    fieldName={fieldName}
                    placeholder={`Enter new ${label}`}
                    disabled={false}
                    readOnly={false}
                    onBlur={addUrl}
                    onPressEnter={addUrl}
                />
            </div>
            <div className="col-4 d-flex align-items-center">
                <IconButton
                    className="url-add-btn"
                    onClick={handleButtonClick}
                    icon={<PlusIcon />}
                    size="xs"
                    disabled={!validateUrl(tagInputValue)}
                />
            </div>
        </div>
    );

    const renderInput = (notRender: boolean): JSX.Element => {
        if (notRender) {
            return <></>;
        }

        return (
            <div className="row mb-5">
                <div className="col-7 col-sm-7 col-md-5 col-lg-4">
                    <WebsiteInput
                        ref={inputRef}
                        value={inputValue}
                        onChangeCallback={(value): void => setInputValue(value)}
                        fieldName={fieldName}
                        placeholder={`Enter new ${label}`}
                        disabled={false}
                        readOnly={false}
                        onBlur={addUrl}
                        onPressEnter={addUrl}
                    />
                </div>
                <div className="col-4 d-flex align-items-center">
                    <IconButton
                        className="url-add-btn"
                        onClick={handleButtonClick}
                        icon={<PlusIcon />}
                        size="xs"
                        disabled={!validateUrl(inputValue)}
                    />
                </div>
            </div>
        );
    };

    const getUrlGroupBody = (): JSX.Element => {
        if (!isAdmin && !urls.length) {
            return <div>No urls</div>;
        }

        if (!isEditMode) {
            return (
                <div>
                    {urls.map((item, i) => (
                        <div key={`url_${i}`} className="fs-6">
                            {item}
                        </div>
                    ))}
                </div>
            );
        }

        if (!formik || !fieldName) {
            return null;
        }

        return (
            <div className="position-relative">
                <div className="" id={fieldName}>
                    <TagGroup>
                        {urls.map((item, i) => (
                            <div key={`url_${i}`}>

                                {i === index ? (
                                    renderTagInput()
                                ) : (
                                    <div className="row mb-5">
                                        <div className="col-7 col-sm-7 col-md-5 col-lg-4">
                                            <Tag
                                                key={i}
                                                closable={isAdmin}
                                                onClose={(e): void => removeUrl(e, item)}
                                                className="written-url"
                                                onClick={(e): void => handleTagClick(e, i)}
                                            >
                                                {item}
                                            </Tag>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {renderInput(urls.length > 0 && formik.touched[fieldName] && formik.errors[fieldName])}
                    </TagGroup>
                </div>
                {getErrorMessage()}
            </div>
        );
    };

    return (
        <div className="url-list">
            {title && (
                <div className={clsx('fs-6 mb-2', !isAdmin && 'text-muted')}>
                    <span>{title}</span>
                    {isRequired && <span className="text-danger ms-1">*</span>}
                </div>
            )}
            {getUrlGroupBody()}
        </div>
    );
};
